'use client';

import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useSetAtom } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { remotePromotion } from '../../atoms';
import { decodeFromBase64 } from '../../utils/decodeFromBase64';
import { clientCookieOptions } from '../../utils/clientCookieOptions';
import { messages } from './PromotionBanner.messages';
import { PromotionBannerEmpty } from './PromotionBannerEmpty';
import type { PromotionBannerProps } from './types';
import { fetchAndActivatePromotion } from './utils/fetch/fetchAndActivatePromotion';
import { usePromotionTrigger } from './utils/promotionTriggerHook';
import { usePromotionBannerData } from './utils/usePromotionBannerData';
import { PromotionStyleWrapper } from './variants/PromotionStyleWrapper';

const DynamicPromotionBannerContentWrapper = dynamic(
  () =>
    import('./PromotionBannerContentWrapper.client').then(
      (mod) => mod.PromotionBannerContentWrapper,
    ),
  { ssr: false },
);

const PromotionBannerContent = ({ promotionData, shopId, variant }: PromotionBannerProps) => {
  // INSPIRE-3716 - implement pending promotion
  const { setCookie } = useCookies();

  const { styleVariant } = useConfig();

  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();

  // Tests and Storybook deliver the data by component-property - all other apps are feeding the swr-cache. So we try both sources here.
  const { data, hasExpiredData = false, isLoading } = usePromotionBannerData(promotionData, shopId);

  // for NJS-1196 allow to redeem voucher by internal SPA-link
  const { P1, BonusNumber, pendingPromo } = Object.fromEntries(useSearchParams()?.entries() ?? []);
  // Once per link, so that redeeming the promotion on this page clears the cookie for good.
  useEffect(() => {
    if ((P1 || BonusNumber) && pendingPromo === 'true') {
      setCookie(
        null,
        'pendingPromo',
        BonusNumber?.toString() || (P1 && decodeFromBase64(P1.toString())),
        clientCookieOptions,
      );
    }
  }, [P1, BonusNumber, pendingPromo, setCookie]);
  const { loggedIn } = useLoginState();
  const setVoucher = useSetAtom(remotePromotion);
  const dataForRender = data?.selected ?? data?.active ?? data?.shop;
  const [showAsPaybackPromotion, setShowAsPaybackPromotion] = useState(
    !!dataForRender?.isPaybackPromotion,
  );
  const { language, defaultLanguage = 'de' } = useI18n();
  usePromotionTrigger((customPromotionCode: string) => {
    fetchAndActivatePromotion(customPromotionCode, setVoucher, language || defaultLanguage);
  });
  useEffect(() => {
    const promotionCode = BonusNumber?.toString() || (P1 && decodeFromBase64(P1.toString()));
    if (promotionCode) {
      fetchAndActivatePromotion(promotionCode, setVoucher, language || defaultLanguage);
    }
    // NJS-1254: name loggedIn here to force rerender and rebuild usePromotionBannerData on login-status-change
  }, [setVoucher, loggedIn, BonusNumber, P1, language, defaultLanguage]);

  // A ref, as StrictMode runs the effect twice on mount and would show the toast twice.
  const expiryNotifiedRef = useRef(false);
  useEffect(() => {
    if (!hasExpiredData) {
      expiryNotifiedRef.current = false;
    } else if (!expiryNotifiedRef.current) {
      expiryNotifiedRef.current = true;
      addNotification({
        severity: 'warning',
        message: formatMessage(messages.expiredTitle),
        duration: 6000,
      });
    }
  }, [hasExpiredData, addNotification, formatMessage]);

  // only render anything if at least one data sample exists
  if (isLoading || !data || (!data.shop && !data.active && !data.selected)) {
    return <PromotionBannerEmpty />;
  }

  const isVoucherBar = data?.shopVoucherBar?.length && data.shopVoucherBar.length > 0;

  const promoContent = (
    <DynamicPromotionBannerContentWrapper
      promotionData={data}
      variant={variant}
      setShowAsPaybackPromotion={setShowAsPaybackPromotion}
    />
  );

  return isVoucherBar ? (
    promoContent
  ) : (
    <PromotionStyleWrapper darkStyle={styleVariant === 'dark'} isPayback={showAsPaybackPromotion}>
      {promoContent}
    </PromotionStyleWrapper>
  );
};

/**
 * The Promotionbanner-Component displays a colored promotion box with interactive features to enable a served promotion
 *
 * @param promotionData Object of shop, active and/or selected promotion data
 * - shop: promotionData by shopId - the corresponding id for e.g. storefront, damenmode, ...
 * - active: promotionData by currently user-active bonuscode (read from session/cookie)
 * - selected: promotionData by user-selected bonuscode - this can be query-Params ?BonusCode or ?P1 (base64 encoded bonuscode)
 * @returns JSX.Element to be placed in PageLayout
 * @throws Error if fetching promotion data fails
 */
export const PromotionBanner = ({
  promotionData,
  shopId,
  variant,
}: PromotionBannerProps): JSX.Element => (
  <PromotionBannerContent promotionData={promotionData} shopId={shopId} variant={variant} />
);
